'use client';
import { _ as _async_to_generator } from "@swc/helpers/_/_async_to_generator";
import { _ as _extends } from "@swc/helpers/_/_extends";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, AlertDescription, AlertTitle } from '@kamio/ui/components/alert';
import { FormControl, FormField, FormItem, FormLabel, FormMessage, Form } from '@kamio/ui/components/form';
import { Input } from '@kamio/ui/components/input';
import { InputOTP, REGEXP_ONLY_DIGITS, InputOTPGroup, InputOTPSlot, InputOTPSeparator } from '@kamio/ui/components/input-otp';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { Captcha } from './captcha';
import { LoadingButton } from '../loading-button';
// Duplication due to nextjs issue, cannot export objects from server functions...
const forgotPasswordSchema = z.object({
    email: z.email({
        message: 'Required'
    }),
    otpRequested: z.boolean().optional(),
    otp: z.string({
        message: 'Required'
    }).min(6, {
        message: 'OTP must be at least 6 characters'
    }).optional(),
    password: z.string({
        message: 'Required'
    }).min(8, {
        message: 'Password must be at least 8 characters'
    }).optional(),
    confirmPassword: z.string({
        message: 'Required'
    }).optional(),
    skipCaptcha: z.boolean().optional(),
    captcha: z.string({
        message: 'Required'
    }).optional()
}).refine((data)=>data.otpRequested ? data.otp !== undefined : true, {
    message: 'OTP is required when requesting a new password',
    path: [
        'otp'
    ]
}).refine((data)=>data.otpRequested ? data.password !== undefined : true, {
    message: 'Password is required when requesting a new password',
    path: [
        'password'
    ]
}).refine((data)=>data.otpRequested ? data.confirmPassword !== undefined : true, {
    message: 'Confirm Password is required when requesting a new password',
    path: [
        'confirmPassword'
    ]
}).refine((data)=>!data.otpRequested || data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: [
        'confirmPassword'
    ]
}).refine((data)=>data.skipCaptcha || data.captcha, {
    message: 'Captcha is required.',
    path: [
        'captcha'
    ]
});
export function ForgotPasswordForm({ defaultValues, captchaSiteKey, redirectTo, forgotPassword, updatePassword, onUpdated }) {
    const router = useRouter();
    const form = useForm({
        resolver: zodResolver(forgotPasswordSchema),
        mode: 'onSubmit',
        defaultValues: _extends({
            otpRequested: false,
            email: ''
        }, defaultValues, {
            skipCaptcha: !captchaSiteKey
        })
    });
    const otpRequested = form.watch('otpRequested');
    function onSubmit(data) {
        return _async_to_generator(function*() {
            try {
                if (otpRequested) {
                    const result = yield updatePassword({
                        email: data.email,
                        otp: data.otp,
                        password: data.password
                    });
                    if (result == null ? void 0 : result.error) {
                        throw new Error(result.error);
                    }
                    toast.success('Password Updated Successfully.');
                    if (redirectTo) {
                        router.push(redirectTo);
                    }
                    onUpdated == null ? void 0 : onUpdated();
                } else {
                    const result = yield forgotPassword({
                        email: data.email,
                        password: data.password
                    });
                    if (result == null ? void 0 : result.error) {
                        throw new Error(result.error);
                    }
                    form.setValue('otpRequested', true);
                    toast.success('An OTP has been sent to you!');
                }
            } catch (ex) {
                console.error(ex);
                if (ex instanceof Error) {
                    toast.error(`An Error Occured: ${ex.message}`);
                }
            }
        })();
    }
    return /*#__PURE__*/ _jsx(Form, _extends({}, form, {
        children: /*#__PURE__*/ _jsx("form", {
            onSubmit: form.handleSubmit(onSubmit),
            className: "animate-in",
            children: /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    form.formState.errors && Object.keys(form.formState.errors).length > 0 && /*#__PURE__*/ _jsxs(Alert, {
                        variant: "destructive",
                        children: [
                            /*#__PURE__*/ _jsx(AlertTitle, {
                                children: "Error"
                            }),
                            /*#__PURE__*/ _jsx(AlertDescription, {
                                children: Object.keys(form.formState.errors).map((key)=>{
                                    const error = form.formState.errors[key];
                                    return /*#__PURE__*/ _jsx("div", {
                                        children: error == null ? void 0 : error.message
                                    }, key);
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(FormField, {
                        control: form.control,
                        name: "email",
                        render: ({ field })=>/*#__PURE__*/ _jsxs(FormItem, {
                                children: [
                                    /*#__PURE__*/ _jsx(FormLabel, {
                                        children: "Email"
                                    }),
                                    /*#__PURE__*/ _jsx(FormControl, {
                                        children: /*#__PURE__*/ _jsx(Input, _extends({}, field, {
                                            autoComplete: "email username",
                                            disabled: otpRequested
                                        }))
                                    }),
                                    /*#__PURE__*/ _jsx(FormMessage, {})
                                ]
                            })
                    }),
                    otpRequested && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(FormField, {
                                control: form.control,
                                name: "otp",
                                render: ({ field })=>/*#__PURE__*/ _jsxs(FormItem, {
                                        children: [
                                            /*#__PURE__*/ _jsx(FormLabel, {
                                                children: "OTP"
                                            }),
                                            /*#__PURE__*/ _jsx(FormControl, {
                                                children: /*#__PURE__*/ _jsx(InputOTP, _extends({
                                                    maxLength: 6,
                                                    pattern: REGEXP_ONLY_DIGITS,
                                                    defaultValue: field.value
                                                }, field, {
                                                    children: /*#__PURE__*/ _jsxs(InputOTPGroup, {
                                                        children: [
                                                            /*#__PURE__*/ _jsx(InputOTPSlot, {
                                                                index: 0
                                                            }),
                                                            /*#__PURE__*/ _jsx(InputOTPSlot, {
                                                                index: 1
                                                            }),
                                                            /*#__PURE__*/ _jsx(InputOTPSlot, {
                                                                index: 2
                                                            }),
                                                            /*#__PURE__*/ _jsx(InputOTPSeparator, {}),
                                                            /*#__PURE__*/ _jsx(InputOTPSlot, {
                                                                index: 3
                                                            }),
                                                            /*#__PURE__*/ _jsx(InputOTPSlot, {
                                                                index: 4
                                                            }),
                                                            /*#__PURE__*/ _jsx(InputOTPSlot, {
                                                                index: 5
                                                            })
                                                        ]
                                                    })
                                                }))
                                            }),
                                            /*#__PURE__*/ _jsx(FormMessage, {})
                                        ]
                                    })
                            }),
                            /*#__PURE__*/ _jsx(FormField, {
                                control: form.control,
                                name: "password",
                                render: ({ field })=>/*#__PURE__*/ _jsxs(FormItem, {
                                        children: [
                                            /*#__PURE__*/ _jsx(FormLabel, {
                                                children: "Password"
                                            }),
                                            /*#__PURE__*/ _jsx(FormControl, {
                                                children: /*#__PURE__*/ _jsx(Input, _extends({}, field, {
                                                    type: "password",
                                                    autoComplete: "new-password"
                                                }))
                                            }),
                                            /*#__PURE__*/ _jsx(FormMessage, {})
                                        ]
                                    })
                            }),
                            /*#__PURE__*/ _jsx(FormField, {
                                control: form.control,
                                name: "confirmPassword",
                                render: ({ field })=>/*#__PURE__*/ _jsxs(FormItem, {
                                        children: [
                                            /*#__PURE__*/ _jsx(FormLabel, {
                                                children: "Confirm Password"
                                            }),
                                            /*#__PURE__*/ _jsx(FormControl, {
                                                children: /*#__PURE__*/ _jsx(Input, _extends({}, field, {
                                                    type: "password",
                                                    autoComplete: "new-password"
                                                }))
                                            }),
                                            /*#__PURE__*/ _jsx(FormMessage, {})
                                        ]
                                    })
                            })
                        ]
                    }),
                    captchaSiteKey && /*#__PURE__*/ _jsx(FormField, {
                        control: form.control,
                        name: "captcha",
                        render: ({ field })=>/*#__PURE__*/ _jsxs(FormItem, {
                                children: [
                                    /*#__PURE__*/ _jsx(FormControl, {
                                        children: /*#__PURE__*/ _jsx("div", {
                                            style: {
                                                display: 'flex',
                                                justifyContent: 'space-around'
                                            },
                                            children: /*#__PURE__*/ _jsx(Captcha, _extends({
                                                siteKey: captchaSiteKey
                                            }, field))
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(FormMessage, {})
                                ]
                            })
                    }),
                    /*#__PURE__*/ _jsx(LoadingButton, {
                        className: "w-full",
                        type: "submit",
                        isLoading: form.formState.isSubmitting,
                        children: otpRequested ? 'Update Password' : 'Request OTP'
                    })
                ]
            })
        })
    }));
}
